import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, ExternalLink, RotateCcw, Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/applyApi";
import { fmtMT } from "@/lib/mountainTime";

const BASE = "/admin/test-applications";
const when = (s) => (s ? fmtMT(s) : "");
const label = (s) => (s || "").replace(/_/g, " ").replace(/^./, (x) => x.toUpperCase());
// Opens the real seller application form on this TEST draft (Apply reads ?draft & t once, then strips them).
const formUrl = (r) => `/apply/${r.slug}?env=test&draft=${encodeURIComponent(r.id)}&t=${encodeURIComponent(r.token)}`;

// Admin-only shortcut: the existing application form on TEST drafts, no invitation or magic link.
export function TestApplicationDialog({ open, onClose }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(BASE).then((r) => setRows(r.data)).catch(() => setRows({ drafts: [], submitted: [] }));
  useEffect(() => { if (open) { setRows(null); load(); } }, [open]);
  // Open the tab synchronously (popup blockers), then point it at the draft.
  const go = async (key, req, msg) => {
    const w = window.open("", "_blank");
    setBusy(key);
    try {
      const r = (await req()).data;
      if (w) w.location.href = formUrl(r); else window.location.assign(formUrl(r));
      if (msg) toast.success(msg);
      load();
    } catch (x) { w?.close(); toast.error(errMsg(x, "Could not open the TEST application")); } finally { setBusy(null); }
  };
  const drafts = rows?.drafts || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="test-app-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-amber-700" />Test Application</DialogTitle>
          <DialogDescription>Opens the real seller application form in a new tab as a TEST application. Nothing is sent to sellers and LIVE horses are never touched. Submitted TEST applications appear in the TEST Pipeline.</DialogDescription>
        </DialogHeader>
        <Button data-testid="test-app-new" disabled={!!busy} onClick={() => go("new", () => api.post(BASE))} className="h-11 rounded-full w-full">
          {busy === "new" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}Start a new TEST application
        </Button>
        {!rows ? <p className="text-sm text-stone-600">Loading TEST applications…</p> : (
          <div className="space-y-4">
            <section>
              <h3 className="text-sm font-semibold text-stone-900 mb-2">Unfinished TEST applications</h3>
              {!drafts.length && <p data-testid="test-app-none" className="text-sm text-stone-600">None yet — start a new one above.</p>}
              <ul className="space-y-2">
                {drafts.map((d, i) => (
                  <li key={d.id} data-testid={`test-app-draft-${i}`} className="rounded-xl border border-stone-200 bg-white p-3">
                    <div className="font-semibold text-stone-900 break-words">{d.horse_name || "Unnamed horse"}{d.contact_name ? <span className="font-normal text-stone-600"> · {d.contact_name}</span> : null}</div>
                    <div className="text-xs text-stone-600">Last saved {when(d.updated_at)}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" data-testid={`test-app-open-${i}`} disabled={!!busy} onClick={() => go(d.id, () => api.post(`${BASE}/${d.id}/open`))} className="rounded-full">
                        <ExternalLink className="h-4 w-4 mr-1" />Continue
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`test-app-reset-${i}`} disabled={!!busy}
                        onClick={() => window.confirm("Reset this TEST application? Its answers, photos and videos are cleared and it opens from the start.") && go(d.id, () => api.post(`${BASE}/${d.id}/reset`), "TEST application reset")}
                        className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Reset &amp; start over</Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            {rows.submitted.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-stone-900 mb-2">Recently submitted (see TEST Pipeline)</h3>
                <ul className="space-y-1">
                  {rows.submitted.map((s, i) => (
                    <li key={s.id} data-testid={`test-app-submitted-${i}`} className="text-sm text-stone-700 flex justify-between gap-2">
                      <span className="break-words">{s.horse_name || "Unnamed horse"}</span><span className="text-stone-500 shrink-0">{label(s.status)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
